import { get, ref } from "firebase/database";
import { db } from "../lib/firebase";
import { getPathDatabaseByRole, renewToken } from "./auth";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { LaunchError } from "../utils/errors";
import {
  PROPOSE_APPLY_TIMEOUT_MS,
  PROPOSE_WARM_UP_TIMEOUT_MS,
  isProposeWarmUpDone,
  markProposeWarmUpDone,
  resetProposeWarmUpDone,
  settleProposeBrandInsightApply,
} from "../utils/brandInsightApplyPropose";
import { APP_SUPPORTED_LANGUAGES } from "../utils/i18nLanguage";
import { getObjectError } from "../utils/string";
import { formatDateForStatistics } from "../utils/date";
import {
  startGetBrandInformation,
  startSaveBrandAllowlistField,
} from "./adsCampaigns";
import { startSaveBrandSetupVersion } from "./brandSetupVersions";
import {
  getDemoBrandLearnedInsights,
  shouldUseDemoBrandLearnedInsights,
} from "../utils/brandInsightsDemo";

const emptyPlaybook = () => ({
  updatedTime: 0,
  lastIngest: null,
  lastSeenAt: 0,
  positive: [],
  negative: [],
});

/**
 * Insights are stored per supported language under i18n. Falls back to
 * another stored language so a card never renders empty.
 */
const insightCopyRank = (code, preferredCode) => {
  if (code === preferredCode) return 0;
  if (code === "es") return 1;
  if (code === "en") return 2;
  return 3;
};

export const getInsightCopy = (insight = {}, language = "es") => {
  const i18n = insight.i18n || {};
  const preferredCode = String(language || "")
    .split("-")[0]
    .toLowerCase();
  const orderedCodes = [
    ...new Set([preferredCode, ...APP_SUPPORTED_LANGUAGES]),
  ].sort(
    (left, right) =>
      insightCopyRank(left, preferredCode) -
      insightCopyRank(right, preferredCode)
  );
  const copy =
    orderedCodes.map((code) => i18n[code]).find((item) => item?.title) || {};
  return {
    title: copy.title || "",
    description: copy.description || "",
  };
};

export const startGetBrandLearnedInsights = () => async (dispatch) => {
  try {
    const { pathBrandLearnedInsights, userID, agencyID } = dispatch(
      getPathDatabaseByRole()
    );
    if (
      shouldUseDemoBrandLearnedInsights({
        advertiserID: userID,
        agencyID,
        shopID: userID,
      })
    ) {
      return { ok: true, data: getDemoBrandLearnedInsights() };
    }
    const snapshot = await get(ref(db, pathBrandLearnedInsights));
    return {
      ok: true,
      data: snapshot.exists() ? snapshot.val() : emptyPlaybook(),
    };
  } catch (error) {
    dispatch(
      LaunchError(error, true, [], {
        functionName: "startGetBrandLearnedInsights",
        context: {},
        tags: { action: "getBrandLearnedInsights" },
        level: "error",
      })
    );
    return { ok: false, data: emptyPlaybook() };
  }
};

export const startIngestBrandLearnedInsights =
  ({ insights, startDateTime, endDateTime }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("ingestBrandLearnedInsights")}/ingestBrandLearnedInsights`,
        data: {
          insights,
          startDateTime: formatDateForStatistics(startDateTime),
          endDateTime: formatDateForStatistics(endDateTime),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startIngestBrandLearnedInsights",
          context: {},
          tags: { action: "ingestBrandLearnedInsights" },
        })
      );
      return getObjectError(error);
    }
  };

export const startMarkBrandInsightsLastSeen = () => async (dispatch) => {
  try {
    const token = await dispatch(renewToken());
    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("markBrandInsightsLastSeen")}/markBrandInsightsLastSeen`,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    dispatch(
      LaunchError(error, false, [], {
        functionName: "startMarkBrandInsightsLastSeen",
        context: {},
        tags: { action: "markBrandInsightsLastSeen" },
      })
    );
    return getObjectError(error);
  }
};

export const startProposeBrandInsightApply =
  ({ insight, currentFields, source, signal }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("proposeBrandInsightApply")}/proposeBrandInsightApply`,
        data: { insight, currentFields, source },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: PROPOSE_APPLY_TIMEOUT_MS,
        signal,
      });
      return settleProposeBrandInsightApply({
        data,
        dispatch,
        launchError: LaunchError,
      });
    } catch (error) {
      return settleProposeBrandInsightApply({
        error,
        dispatch,
        launchError: LaunchError,
      });
    }
  };

/**
 * Boots the proposeBrandInsightApply instance while the user reads the page.
 * Cold starts cost 17-20s, so paying it on mount hides it behind the time it
 * takes to pick an insight. Silent by design: a warm-up failure must never
 * alert, retry, or reach Sentry.
 */
export const startWarmUpProposeBrandInsightApply = () => async (dispatch) => {
  if (isProposeWarmUpDone()) {
    return { ok: false };
  }
  // Marked before the request so a double mount (StrictMode) cannot fire twice.
  // Cleared on failure so the next visit retries instead of staying cold.
  markProposeWarmUpDone();
  try {
    const token = await dispatch(renewToken());
    if (!token) {
      resetProposeWarmUpDone();
      return { ok: false };
    }
    const { data } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("proposeBrandInsightApply")}/proposeBrandInsightApply`,
      data: { warmUp: true },
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: PROPOSE_WARM_UP_TIMEOUT_MS,
    });
    if (!data?.ok) {
      resetProposeWarmUpDone();
      return { ok: false };
    }
    return { ok: true };
  } catch (error) {
    resetProposeWarmUpDone();
    return { ok: false };
  }
};

/**
 * Removes the insight at polarity/index. The server remembers its topicKey
 * so a later ingest never brings it back.
 */
export const startDismissBrandLearnedInsight =
  ({ polarity, index }) =>
  async (dispatch) => {
    try {
      const token = await dispatch(renewToken());
      const { data } = await axios({
        method: "post",
        url: `${getCloudFunctionUrl("dismissBrandLearnedInsight")}/dismissBrandLearnedInsight`,
        data: { polarity, index },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return data;
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startDismissBrandLearnedInsight",
          context: { polarity, index },
          tags: { action: "dismissBrandLearnedInsight" },
          level: "error",
        })
      );
      return getObjectError(error);
    }
  };

/**
 * Writes the proposed field and versions brand setup. If the insight lives in
 * the playbook (polarity + index), dismisses it so a later ingest cannot bring
 * it back. Report-sourced applies have no playbook entry.
 */
export const startConfirmBrandInsightApply =
  ({ field, proposedValue, previousBrand, polarity, index }) =>
  async (dispatch) => {
    try {
      const saveResult = await dispatch(
        startSaveBrandAllowlistField(field, proposedValue)
      );
      if (!saveResult?.ok) {
        return { ok: false };
      }

      const apiData = {
        ...(previousBrand || {}),
        [field]: proposedValue,
      };
      await dispatch(
        startSaveBrandSetupVersion({
          apiData,
          previousApiData: previousBrand,
          source: "insight_apply",
        })
      );

      await dispatch(startGetBrandInformation());

      const hasPlaybookEntry = Boolean(polarity) && Number.isInteger(index);
      if (!hasPlaybookEntry) {
        return { ok: true };
      }

      const dismissed = await dispatch(
        startDismissBrandLearnedInsight({ polarity, index })
      );
      if (!dismissed?.ok) {
        return { ok: false };
      }

      return { ok: true, data: dismissed.data };
    } catch (error) {
      dispatch(
        LaunchError(error, true, [], {
          functionName: "startConfirmBrandInsightApply",
          context: { field, polarity, index },
          tags: { action: "confirmBrandInsightApply" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
